import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Robot, ChartLineUp, Spinner, Lightning } from "@phosphor-icons/react";


function fmtUSD(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0.00";
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTokens(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

const FEATURE_LABELS = {
  ai_chat: "AI Chat (Grok)",
  asset_analysis: "Asset Analysis",
  room_bot: "#GNBOT replies",
};


export default function AIUsagePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/ai-usage?months=6`, { withCredentials: true });
        if (!mounted) return;
        setData(r.data);
        setAllowed(true);
      } catch (err) {
        // Silently hide for non-admins
        if (mounted) setAllowed(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const maxCost = useMemo(() => {
    if (!data?.history?.length) return 0;
    return Math.max(...data.history.map(h => h.cost_usd || 0), 0.000001);
  }, [data]);

  if (loading || allowed === null) return null;
  if (!allowed) return null;

  return (
    <div className="bg-zinc-900/50 border border-amber-500/30 rounded-sm p-6" data-testid="ai-usage-panel">
      <div className="flex items-center gap-2 mb-1">
        <Robot size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-zinc-200">Admin · Grok / AI Spend</h3>
        <Badge className="text-[8px] px-1.5 py-0 h-4 border-0 bg-amber-900/30 text-amber-400">ADMIN</Badge>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4">
        Token usage and approximate USD cost across every AI call. Pricing assumes
        ${data.pricing.input_per_million_usd.toFixed(2)}/M input and
        ${data.pricing.output_per_million_usd.toFixed(2)}/M output for {data.history[0]?.year_month ? "grok-3-mini" : "the configured model"}.
      </p>

      {/* Current-month tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile
          label="This Month"
          value={fmtUSD(data.current_month.cost_usd)}
          sub={`${data.current_month.calls} calls · ${fmtTokens(data.current_month.total_tokens)} tok`}
          accent="emerald"
          testid="ai-usage-current"
        />
        <Tile
          label="All Time"
          value={fmtUSD(data.all_time.cost_usd)}
          sub={`${data.all_time.calls} calls · ${fmtTokens(data.all_time.total_tokens)} tok`}
          accent="amber"
        />
        <Tile
          label="Input Tokens (mo)"
          value={fmtTokens(data.current_month.prompt_tokens)}
          sub={`${fmtUSD(data.pricing.input_per_million_usd)} per 1M`}
          accent="zinc"
        />
        <Tile
          label="Output Tokens (mo)"
          value={fmtTokens(data.current_month.completion_tokens)}
          sub={`${fmtUSD(data.pricing.output_per_million_usd)} per 1M`}
          accent="zinc"
        />
      </div>

      {/* History bar chart */}
      {data.history.length > 0 && (
        <div className="mb-5" data-testid="ai-usage-history">
          <div className="flex items-center gap-2 mb-2">
            <ChartLineUp size={12} className="text-zinc-500" />
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Last {data.history.length} months</span>
          </div>
          <div className="flex items-end gap-2 h-24 border border-zinc-800 rounded-sm bg-zinc-950/50 p-3">
            {data.history.map((h) => {
              const pct = maxCost > 0 ? Math.max(2, (h.cost_usd / maxCost) * 100) : 2;
              const isCurrent = h.year_month === data.current_month.year_month;
              return (
                <div key={h.year_month} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
                  <span className={`text-[9px] font-data ${isCurrent ? "text-amber-300" : "text-zinc-500"}`}>
                    {fmtUSD(h.cost_usd)}
                  </span>
                  <div
                    className={`w-full rounded-sm transition-all ${isCurrent ? "bg-amber-400/80" : "bg-zinc-700/70"}`}
                    style={{ height: `${pct}%` }}
                    title={`${h.year_month}: ${fmtUSD(h.cost_usd)} · ${h.calls} calls`}
                  />
                  <span className="text-[9px] text-zinc-600 truncate w-full text-center">
                    {h.year_month.slice(5)}/{h.year_month.slice(2, 4)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By feature & top users side-by-side */}
      <div className="grid md:grid-cols-2 gap-4">
        <div data-testid="ai-usage-by-feature">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={12} className="text-zinc-500" />
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">By feature (this month)</span>
          </div>
          {data.by_feature.length === 0 ? (
            <p className="text-[11px] text-zinc-600 italic">No usage yet this month.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.by_feature.map((f) => (
                <li key={f.feature} className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-300">{FEATURE_LABELS[f.feature] || f.feature}</span>
                  <span className="font-data text-amber-300">
                    {fmtUSD(f.cost_usd)} <span className="text-zinc-600">· {f.calls}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div data-testid="ai-usage-top-users">
          <div className="flex items-center gap-2 mb-2">
            <Robot size={12} className="text-zinc-500" />
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Top spenders (this month)</span>
          </div>
          {data.top_users.length === 0 ? (
            <p className="text-[11px] text-zinc-600 italic">No user-attributed calls yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.top_users.map((u) => (
                <li key={u.email} className="flex items-center justify-between text-[11px] gap-2">
                  <span className="text-zinc-300 truncate" title={u.email}>{u.email}</span>
                  <span className="font-data text-amber-300 whitespace-nowrap">
                    {fmtUSD(u.cost_usd)} <span className="text-zinc-600">· {fmtTokens(u.total_tokens)}t</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


function Tile({ label, value, sub, accent = "zinc", testid }) {
  const accents = {
    amber: "border-amber-500/30 text-amber-300",
    emerald: "border-emerald-500/30 text-emerald-300",
    zinc: "border-zinc-800 text-zinc-200",
  };
  return (
    <div
      className={`bg-zinc-950/50 border ${accents[accent]} rounded-sm p-3`}
      data-testid={testid}
    >
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-data font-semibold leading-tight">{value}</div>
      <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>
    </div>
  );
}

// Spinner re-exported so tree-shaker doesn't drop the icon (used during loading state)
export { Spinner };
